// src/pages/SearchLeadsPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Card,
  CardContent,
  Typography,
  Chip,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Pagination,
  Checkbox,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import TuneIcon from "@mui/icons-material/Tune";
import EditIcon from "@mui/icons-material/Edit";
import { navigateToLeadDetail } from "../utils/leadDetailNavigation";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/Delete";
import API from "../utils/api";
import { toast } from "react-toastify";
import LeadStatusCell from "../components/LeadStatusCell";
import VisibilityIcon from "@mui/icons-material/Visibility";
import useAuth from "../hooks/useAuth";
import ConfirmDialog from "../components/ConfirmDialog";
import { LeadStatusFilterMenuItems } from "../components/LeadStatusMenuItems";
import DisabledButtonWrapper from "../components/DisabledButtonWrapper";
import { displayLeadCategoryLabel } from "../utils/leadCategoryLabel";
import {
  getLeadCategoryOutlineColor,
  leadCategoryChipSx,
} from "../utils/leadCategoryChip";

const SEARCH_LEADS_STATE_KEY = "searchLeadsState";
const ROWS_PER_PAGE_OPTIONS = [15, 25, 50, 100];

/** Outlined Previous/Next: primary fill + white text on hover */
const paginationNavButtonHoverSx = {
  "&:hover:not(.Mui-disabled)": {
    bgcolor: "primary.main",
    color: "primary.contrastText",
    borderColor: "primary.main",
  },
};

const defaultFilters = {
  status: "all",
  category: "all",
  territory: "all",
  assigned_rep: "all",
  property_type: "all",
  start_date: "",
  end_date: "",
  archived: "false",
  proposal_status: "all",
};

const defaultRefine = {
  first_name: "",
  last_name: "",
  address: "",
  zip_code: "",
  phone: "",
};

function sanitizeRefine(raw) {
  if (!raw || typeof raw !== "object") return { ...defaultRefine };
  return {
    first_name: typeof raw.first_name === "string" ? raw.first_name : "",
    last_name: typeof raw.last_name === "string" ? raw.last_name : "",
    address: typeof raw.address === "string" ? raw.address : "",
    zip_code: typeof raw.zip_code === "string" ? raw.zip_code : "",
    phone: typeof raw.phone === "string" ? raw.phone : "",
  };
}

function countFilledRefine(refine) {
  return Object.values(refine || {}).filter((v) => String(v || "").trim()).length;
}

function getInitialSearchState() {
  try {
    const raw = sessionStorage.getItem(SEARCH_LEADS_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      searchText: typeof parsed.searchText === "string" ? parsed.searchText : "",
      filters: { ...defaultFilters, ...(parsed.filters && typeof parsed.filters === "object" ? parsed.filters : {}) },
      refine: sanitizeRefine(parsed.refine),
      page: typeof parsed.page === "number" && parsed.page >= 0 ? parsed.page : 0,
      rowsPerPage: typeof parsed.rowsPerPage === "number" && parsed.rowsPerPage > 0 ? parsed.rowsPerPage : 15,
    };
  } catch {
    return null;
  }
}

export default function SearchLeadsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { role, isReadOnly } = useAuth();
  const canViewCategory = role !== "Sales Rep";
  const canDeleteLeads =
    (role === "Admin" || role === "Sales Director") && !isReadOnly;

  const [leads, setLeads] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [territories, setTerritories] = useState([]);
  const [users, setUsers] = useState([]);
  const [leadCategories, setLeadCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search and filter state (initialized from sessionStorage when returning from lead detail)
  const [searchText, setSearchText] = useState(() => {
    const s = getInitialSearchState();
    return s?.searchText ?? "";
  });
  const [filters, setFilters] = useState(() => {
    const s = getInitialSearchState();
    return s?.filters ?? defaultFilters;
  });
  const [refine, setRefine] = useState(() => {
    const s = getInitialSearchState();
    return s?.refine ?? { ...defaultRefine };
  });

  const [showFilters, setShowFilters] = useState(false);
  const [showRefine, setShowRefine] = useState(() => {
    const s = getInitialSearchState();
    return countFilledRefine(s?.refine) > 0;
  });
  const [page, setPage] = useState(() => {
    const s = getInitialSearchState();
    return s?.page ?? 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const s = getInitialSearchState();
    return s?.rowsPerPage ?? 15;
  });
  const [archiving, setArchiving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const querySignature = useMemo(
    () => JSON.stringify({ searchText, filters, refine }),
    [searchText, filters, refine]
  );
  const prevQuerySigRef = useRef(null);

  // Mobile card styling (same pattern as TerritoriesPage)
  const cardSx = {
    borderRadius: 3,
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    boxShadow: "none",
  };

  const mobileLeadCardSx = {
    p: 2.25,
    borderRadius: 3,
    bgcolor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(30,41,59,0.88)"
        : "background.paper",
    border: "1px solid",
    borderColor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.06)",
    boxShadow: (theme) =>
      theme.palette.mode === "dark"
        ? "0 8px 24px rgba(0,0,0,0.55)"
        : "0 4px 12px rgba(0,0,0,0.12)",
    cursor: "pointer",
    "&:active": {
      transform: "scale(0.995)",
    },
  };



  // Persist search and filter state to sessionStorage (restored when returning from lead detail)
  useEffect(() => {
    try {
      sessionStorage.setItem(
        SEARCH_LEADS_STATE_KEY,
        JSON.stringify({ searchText, filters, refine, page, rowsPerPage })
      );
    } catch {
      // ignore quota or parse errors
    }
  }, [searchText, filters, refine, page, rowsPerPage]);

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [territoriesRes, usersRes] = await Promise.all([
          API.get("/territories/"),
          API.get("/users/"),
        ]);
        setTerritories(territoriesRes.data);
        setUsers(usersRes.data);
      } catch (err) {
        console.error("Error fetching dropdown data:", err);
        toast.error("Failed to load filter options");
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!canViewCategory) return;
      try {
        const res = await API.get("/lead-categories/");
        const rows = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setLeadCategories(rows);
      } catch (err) {
        console.error("Error fetching lead categories:", err);
      }
    };
    fetchCategories();
  }, [canViewCategory]);

  // Fetch leads with search and filters
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const filledRefine = Object.fromEntries(
        Object.entries(refine).filter(([, v]) => String(v || "").trim())
      );
      const refineActive = Object.keys(filledRefine).length > 0;

      if (refineActive) {
        Object.entries(filledRefine).forEach(([key, value]) => {
          params.append(key, String(value).trim());
        });
      } else if (searchText.trim()) {
        params.append("search", searchText.trim());
      }

      if (filters.status && filters.status !== "all") {
        params.append("status", filters.status);
      }
      if (filters.category && filters.category !== "all") {
        params.append("category", filters.category);
      }
      if (filters.territory && filters.territory !== "all") {
        params.append("territory", filters.territory);
      }
      if (filters.assigned_rep && filters.assigned_rep !== "all") {
        params.append("assigned_rep", filters.assigned_rep);
      }
      if (filters.property_type && filters.property_type !== "all") {
        params.append("property_type", filters.property_type);
      }
      if (filters.proposal_status && filters.proposal_status !== "all") {
        params.append("proposal_status", filters.proposal_status);
      }
      if (filters.start_date) {
        params.append("start_date", filters.start_date);
      }
      if (filters.end_date) {
        params.append("end_date", filters.end_date);
      }
      
      // Add archived filter (Admin/Sales Director only)
      if (filters.archived === "true") {
        params.append("archived", "true");
      }

      // Server-side pagination (API only paginates when ?page= is sent)
      params.append("page", String(page + 1));
      params.append("page_size", String(rowsPerPage));

      const queryString = params.toString();
      const url = `/leads/${queryString ? `?${queryString}` : ""}`;
      const res = await API.get(url);

      const leadsData = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setLeads(leadsData);
      const count = res.pagination?.count;
      setTotalCount(
        typeof count === "number" ? count : leadsData.length
      );
    } catch (err) {
      console.error("Error fetching leads:", err);
      toast.error("Failed to load leads");
      setLeads([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [searchText, filters, refine, page, rowsPerPage]);

  useEffect(() => {
    const queryChanged = prevQuerySigRef.current !== querySignature;
    if (prevQuerySigRef.current === null) {
      prevQuerySigRef.current = querySignature;
    } else if (queryChanged) {
      prevQuerySigRef.current = querySignature;
      setSelectedLeadIds([]);
      if (page !== 0) {
        setPage(0);
        return;
      }
    }
    const delay = queryChanged ? 300 : 0;
    const timer = setTimeout(() => {
      fetchLeads();
    }, delay);
    return () => clearTimeout(timer);
  }, [querySignature, page, rowsPerPage, fetchLeads]);

  const handleRowClick = (lead) => {
    if (!lead?.id) return;
    navigateToLeadDetail(navigate, lead.id, {
      pathname: "/leads/search",
      search: location.search,
      label: "Search Leads",
    });
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setSearchText("");
    setFilters(defaultFilters);
    setRefine({ ...defaultRefine });
    setPage(0);
    try {
      sessionStorage.removeItem(SEARCH_LEADS_STATE_KEY);
    } catch {
      // ignore
    }
  };

  const handleRefineChange = (field, value) => {
    setRefine((prev) => ({ ...prev, [field]: value }));
  };

  const handleArchiveOldLeads = async () => {
    if (isReadOnly) return;
    
    setArchiving(true);
    try {
      const res = await API.post("/leads/archive-old-leads/");
      const data = res.data;
      
      toast.success(
        `✅ ${data.message || 'Archive process completed successfully'}`,
        { autoClose: 5000 }
      );
      
      // Refresh the leads list
      await fetchLeads();
      setConfirmArchive(false);
    } catch (err) {
      console.error("Archive error:", err);
      const errorMsg = err.response?.data?.detail || "Failed to archive old leads";
      toast.error(errorMsg);
    } finally {
      setArchiving(false);
    }
  };

  const visibleLeadIds = useMemo(
    () => leads.map((lead) => lead.id).filter(Boolean),
    [leads]
  );

  const allVisibleSelected =
    canDeleteLeads &&
    visibleLeadIds.length > 0 &&
    visibleLeadIds.every((id) => selectedLeadIds.includes(id));

  const someVisibleSelected =
    canDeleteLeads &&
    visibleLeadIds.some((id) => selectedLeadIds.includes(id));

  const handleToggleLeadSelection = (leadId, event) => {
    event.stopPropagation();
    setSelectedLeadIds((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleToggleSelectAllVisible = (event) => {
    event.stopPropagation();
    if (allVisibleSelected) {
      setSelectedLeadIds((prev) =>
        prev.filter((id) => !visibleLeadIds.includes(id))
      );
    } else {
      setSelectedLeadIds((prev) =>
        Array.from(new Set([...prev, ...visibleLeadIds]))
      );
    }
  };

  const handleBulkDeleteLeads = async () => {
    if (!canDeleteLeads || selectedLeadIds.length === 0) return;

    setBulkDeleting(true);
    try {
      const res = await API.post("/leads/bulk-delete/", { ids: selectedLeadIds });
      const data = res.data;
      toast.success(data.message || "Lead(s) archived successfully");
      setSelectedLeadIds([]);
      setConfirmBulkDelete(false);
      await fetchLeads();
    } catch (err) {
      console.error("Bulk delete error:", err);
      const errorMsg =
        err.response?.data?.detail || "Failed to archive selected leads";
      toast.error(errorMsg);
    } finally {
      setBulkDeleting(false);
    }
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.status !== "all") count++;
    if (filters.category && filters.category !== "all") count++;
    if (filters.territory !== "all") count++;
    if (filters.assigned_rep !== "all") count++;
    if (filters.property_type !== "all") count++;
    if (filters.proposal_status !== "all") count++;
    if (filters.start_date) count++;
    if (filters.end_date) count++;
    if (filters.archived === "true") count++; // Count archived filter
    return count;
  }, [filters]);

  const activeRefineCount = useMemo(() => countFilledRefine(refine), [refine]);
  const refineActive = activeRefineCount > 0;

  // Get unique property types from leads
  const propertyTypes = useMemo(() => {
    const types = leads
      .map((lead) => lead.property_type)
      .filter(Boolean)
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .sort();
    return types;
  }, [leads]);

  const totalPages = useMemo(() => {
    if (totalCount === 0) return 1;
    return Math.max(1, Math.ceil(totalCount / rowsPerPage));
  }, [totalCount, rowsPerPage]);

  const rangeStart = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const rangeEnd = Math.min((page + 1) * rowsPerPage, totalCount);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };

  useEffect(() => {
    setSelectedLeadIds([]);
  }, [page, rowsPerPage]);

  // If result set shrinks, stay on a valid page (e.g. restored session page too high)
  useEffect(() => {
    if (totalCount === 0) return;
    const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [totalCount, rowsPerPage, page]);

  // Helper to format full name
  const getFullName = (lead) => {
    const first = lead?.first_name || "";
    const last = lead?.last_name || "";
    return `${first} ${last}`.trim() || "—";
  };

  // Helper to format full address
  const getFullAddress = (lead) => {
    const parts = [
      lead?.address,
      lead?.city,
      lead?.state,
      lead?.zip_code,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "—";
  };

  return (
    <Box sx={{ p: 3, px: { xs: 1, sm: 3 } }}>
      <Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
        {/* Header Card */}
        <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <SearchIcon color="primary" />
              </Box>

              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Search Leads
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Search and filter leads across all territories. Click a lead to view details.
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Card>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* ----------------------------------------------------------------Search Bar */}
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <TextField
		data-tour="search-bar"
                fullWidth
                placeholder="Search by address or customer name"
                helperText={
                  refineActive
                    ? "Simple search is paused while Refine search is in use"
                    : "Search for address or customer's name. Use Refine search for more detailed results."
                }
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                disabled={refineActive}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                  endAdornment: searchText && !refineActive && (
                    <Button
                      size="small"
                      onClick={() => setSearchText("")}
                      sx={{ minWidth: "auto", p: 0.5 }}
                    >
                      <ClearIcon fontSize="small" />
                    </Button>
                  ),
                }}
              />

              {/* ---------------------------------------------------------------Filter Toggle Button */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  width: "100%",
                }}
              >
                <Button
		 data-tour="filter-toggle"
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  size="small"
                >
                  {showFilters ? "Hide Filters" : "Show Filters"}
                  {activeFilterCount > 0 && (
                    <Chip
                      label={activeFilterCount}
                      size="small"
                      sx={{
                        ml: 1,
                        height: 20,
                        minWidth: 20,
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                      }}
                    />
                  )}
                </Button>
                <Button
                  data-tour="refine-toggle"
                  variant="outlined"
                  startIcon={<TuneIcon />}
                  onClick={() => setShowRefine(!showRefine)}
                  size="small"
                >
                  {showRefine ? "Hide Refine" : "Refine Search"}
                  {activeRefineCount > 0 && (
                    <Chip
                      label={activeRefineCount}
                      size="small"
                      sx={{
                        ml: 1,
                        height: 20,
                        minWidth: 20,
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                      }}
                    />
                  )}
                </Button>
                
                {/* Archive Old Leads Button - Admin/Sales Director Only */}
                {/* ✅ Always visible for Admin/Sales Director (for training), disabled when read-only */}
                {(role === "Admin" || role === "Sales Director") && (
                  <DisabledButtonWrapper 
                    isDisabled={isReadOnly || archiving}
                    tooltipText={archiving ? "Archiving in progress..." : "Read-only mode: This action is disabled"}
                  >
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      startIcon={<ArchiveIcon />}
                      onClick={() => {
                        if (isReadOnly || archiving) return; // Kill the click
                        setConfirmArchive(true);
                      }}
                      disabled={isReadOnly || archiving}
                      sx={{
                        borderColor: "warning.main",
                        color: "warning.main",
                        opacity: (isReadOnly || archiving) ? 0.6 : 1,
                        cursor: (isReadOnly || archiving) ? 'not-allowed' : 'pointer',
                        visibility: 'visible', // ✅ Explicitly ensure visibility
                        display: 'inline-flex', // ✅ Ensure button is displayed
                        "&:hover": {
                          borderColor: "warning.dark",
                          bgcolor: (isReadOnly || archiving) ? "transparent" : "warning.light",
                        },
                      }}
                    >
                      {archiving ? "Archiving..." : "Archive Old Leads"}
                    </Button>
                  </DisabledButtonWrapper>
                )}
                {canDeleteLeads && selectedLeadIds.length > 0 && (
                  <DisabledButtonWrapper
                    isDisabled={bulkDeleting}
                    tooltipText={bulkDeleting ? "Archiving selected leads..." : ""}
                  >
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        if (bulkDeleting) return;
                        setConfirmBulkDelete(true);
                      }}
                      disabled={bulkDeleting}
                    >
                      {bulkDeleting
                        ? "Archiving..."
                        : `Delete Selected (${selectedLeadIds.length})`}
                    </Button>
                  </DisabledButtonWrapper>
                )}
                {(activeFilterCount > 0 || activeRefineCount > 0 || Boolean(searchText.trim())) && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleClearFilters}
                    startIcon={<ClearIcon />}
                    sx={{ ml: "auto" }}
                  >
                    Clear All
                  </Button>
                )}
              </Box>

              {/* --------------------------------------------------Advanced Filters */}
              {showFilters && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                      md: "1fr 1fr 1fr",
                    },
                    gap: 2,
                    pt: 2,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      label="Status"
                      onChange={(e) => handleFilterChange("status", e.target.value)}
                    >
                      <LeadStatusFilterMenuItems />
                    </Select>
                  </FormControl>

                  {canViewCategory && (
                    <FormControl size="small" fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={filters.category || "all"}
                        label="Category"
                        onChange={(e) => handleFilterChange("category", e.target.value)}
                      >
                        <MenuItem value="all">All Categories</MenuItem>
                        {leadCategories.map((cat) => (
                          <MenuItem key={cat.id} value={String(cat.id)}>
                            {displayLeadCategoryLabel(cat.label)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <FormControl size="small" fullWidth>
                    <InputLabel>Territory</InputLabel>
                    <Select
                      value={filters.territory}
                      label="Territory"
                      onChange={(e) => handleFilterChange("territory", e.target.value)}
                    >
                      <MenuItem value="all">All Territories</MenuItem>
                      {territories.map((territory) => (
                        <MenuItem key={territory.id} value={territory.id}>
                          {territory.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth>
                    <InputLabel>Assigned Rep</InputLabel>
                    <Select
                      value={filters.assigned_rep}
                      label="Assigned Rep"
                      onChange={(e) => handleFilterChange("assigned_rep", e.target.value)}
                    >
                      <MenuItem value="all">All Reps</MenuItem>
                      {users
                        .filter((user) => user.groups?.some((g) => typeof g === "string" ? g.includes("Rep") : g.name?.includes("Rep")))
                        .map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.full_name || user.username}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth>
                    <InputLabel>Property Type</InputLabel>
                    <Select
                      value={filters.property_type}
                      label="Property Type"
                      onChange={(e) => handleFilterChange("property_type", e.target.value)}
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      {propertyTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth>
                    <InputLabel>Proposal Status</InputLabel>
                    <Select
                      value={filters.proposal_status}
                      label="Proposal Status"
                      onChange={(e) => handleFilterChange("proposal_status", e.target.value)}
                    >
                      <MenuItem value="all">All Proposals</MenuItem>
                      <MenuItem value="draft">Draft (Not Sent)</MenuItem>
                      <MenuItem value="sent">Sent</MenuItem>
                      <MenuItem value="accepted">Accepted</MenuItem>
                      <MenuItem value="none">No Proposal</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    size="small"
                    label="Start Date"
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => handleFilterChange("start_date", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />

                  <TextField
                    size="small"
                    label="End Date"
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => handleFilterChange("end_date", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />

                  {/* Archived Filter - Admin/Sales Director Only */}
                  {(role === "Admin" || role === "Sales Director") && (
                    <FormControl size="small" fullWidth>
                      <InputLabel>Lead Status</InputLabel>
                      <Select
                        value={filters.archived}
                        label="Lead Status"
                        onChange={(e) => handleFilterChange("archived", e.target.value)}
                      >
                        <MenuItem value="false">Active Leads</MenuItem>
                        <MenuItem value="true">Archived Leads</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>
              )}

              {showRefine && (
                <Box
                  data-tour="refine-search"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                      md: "1fr 1fr 1fr",
                    },
                    gap: 2,
                    pt: 2,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <TextField
                    size="small"
                    label="First name"
                    value={refine.first_name}
                    onChange={(e) => handleRefineChange("first_name", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Last name"
                    value={refine.last_name}
                    onChange={(e) => handleRefineChange("last_name", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Address"
                    value={refine.address}
                    onChange={(e) => handleRefineChange("address", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Zip code"
                    value={refine.zip_code}
                    onChange={(e) => handleRefineChange("zip_code", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Phone"
                    value={refine.phone}
                    onChange={(e) => handleRefineChange("phone", e.target.value)}
                    fullWidth
                  />
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* ---------------------------------------------------Results Table */}
       <Card sx={{ ...cardSx, overflow: "visible" }}>

          {loading ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography>Loading leads...</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ width: "100%", overflow: "auto" }}>
                {isMobile ? (
		<>
            
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
                    {leads.length === 0 ? (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography color="text.secondary" gutterBottom>No leads found</Typography>
                        <Button variant="contained" color="primary" size="small" onClick={() => navigate("/territory/add/lead")} sx={{ mt: 1 }}>
                          Add a lead
                        </Button>
                      </Box>
                    ) : (
                      leads.map((lead) => (
                        <Card
                          key={lead.id}
                          onClick={() => handleRowClick(lead)}
                          sx={{
                            ...mobileLeadCardSx,
                            position: "relative",
                          }}
                        >
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {/* Identity Row */}
                            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                              {/* Avatar */}
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  bgcolor: "rgba(25,118,210,0.12)",
                                  color: "primary.main",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  flexShrink: 0
                                }}
                              >
                                {getFullName(lead)
                                  .split(" ")
                                  .map(n => n[0])
                                  .slice(0,2)
                                  .join("")}
                              </Box>

                              {/* Name + Address */}
                              <Box>
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  sx={{ color: "primary.main", lineHeight: 1.2 }}
                                >
                                  {getFullName(lead)}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontSize: 13 }}
                                >
                                  {getFullAddress(lead)}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Stats Grid */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 1.5,
                                fontSize: 13
                              }}
                            >
                              {/* Status */}
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Status
                                </Typography>
                                <Box>
                                  <LeadStatusCell status={lead.status} />
                                </Box>
                              </Box>

                              {/* Territory */}
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Territory
                                </Typography>
                                <Typography variant="body2">
                                  {lead.territory_name || "—"}
                                </Typography>
                              </Box>

                              {/* Category */}
                              {canViewCategory && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Category
                                  </Typography>
                                  <Box sx={{ mt: 0.25 }}>
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={displayLeadCategoryLabel(lead.category_label)}
                                      sx={leadCategoryChipSx(getLeadCategoryOutlineColor(lead))}
                                    />
                                  </Box>
                                </Box>
                              )}

                              {/* Phone */}
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Phone
                                </Typography>
                                <Typography variant="body2">
                                  {lead.phone || "—"}
                                </Typography>
                              </Box>

                              {/* Assigned Rep */}
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Rep
                                </Typography>
                                <Typography variant="body2">
                                  {lead.assigned_rep_name || "—"}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                          
                          {/* -----------------------------------------Visibility Icon - positioned absolutely at bottom right */}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent card click
                              handleRowClick(lead);
                            }}
                            sx={{
                              position: "absolute",
                              top: 10,
                              right: 8,
                              bgcolor: "background.paper",
                              boxShadow: 1,
                              "&:hover": {
                                bgcolor: "action.hover",
                              },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Card>
                      ))
                    )}
                  </Box>

  {isMobile && leads.length > 0 && (
    <Box
      sx={{
        px: 2,
        py: 1.75,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.5,
        borderTop: 1,
        borderColor: "divider",
        bgcolor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.04)"
            : "grey.50",
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign="center"
        sx={{ width: "100%" }}
      >
        Showing {rangeStart}-{rangeEnd} of {totalCount}{" "}
        {totalCount === 1 ? "lead" : "leads"}
      </Typography>
      <Stack
        direction="row"
        flexWrap="wrap"
        justifyContent="center"
        alignItems="center"
        spacing={1}
        useFlexGap
        sx={{ width: "100%", maxWidth: 420 }}
      >
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel id="search-leads-rows-mobile-label">Rows</InputLabel>
          <Select
            labelId="search-leads-rows-mobile-label"
            label="Rows"
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
          >
            {ROWS_PER_PAGE_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          disabled={page === 0}
          onClick={() => handleChangePage(null, page - 1)}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
            minWidth: 88,
            ...paginationNavButtonHoverSx,
          }}
        >
          Previous
        </Button>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            minWidth: 52,
            textAlign: "center",
            fontWeight: 600,
            px: 0.5,
          }}
        >
          {page + 1} / {totalPages}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          disabled={page >= totalPages - 1}
          onClick={() => handleChangePage(null, page + 1)}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
            minWidth: 88,
            ...paginationNavButtonHoverSx,
          }}
        >
          Next
        </Button>
      </Stack>
    </Box>
  )}

</>


                ) : (
                  // Desktop Table View
                  <>
                    <Table
data-tour="results-table">
                      <TableHead>
                        <TableRow
                          sx={{
                            bgcolor: theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.04)"
                              : "grey.50",
                          }}
                        >
                          {canDeleteLeads && (
                            <TableCell padding="checkbox" sx={{ width: 48 }}>
                              <Checkbox
                                size="small"
                                checked={allVisibleSelected}
                                indeterminate={someVisibleSelected && !allVisibleSelected}
                                onClick={handleToggleSelectAllVisible}
                                inputProps={{ "aria-label": "Select all visible leads" }}
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                            Name
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                            Email
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                            Phone
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                            Address
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                            Status
                          </TableCell>
                          {canViewCategory && (
                            <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                              Category
                            </TableCell>
                          )}
                          <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                            Territory
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                            Assigned Rep
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                            Created
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {leads.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={(canViewCategory ? 9 : 8) + (canDeleteLeads ? 1 : 0)}
                              sx={{ textAlign: "center", py: 4 }}
                            >
                              <Typography color="text.secondary" display="block" gutterBottom>No leads found</Typography>
                              <Button variant="contained" color="primary" size="small" onClick={() => navigate("/territory/add/lead")}>
                                Add a lead
                              </Button>
                            </TableCell>
                          </TableRow>
                        ) : (
                          leads.map((lead) => (
                            <TableRow
                              key={lead.id}
                              hover
                              onClick={() => handleRowClick(lead)}
                              sx={{ cursor: "pointer" }}
                            >
                              {canDeleteLeads && (
                                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    size="small"
                                    checked={selectedLeadIds.includes(lead.id)}
                                    onClick={(e) => handleToggleLeadSelection(lead.id, e)}
                                    inputProps={{ "aria-label": `Select ${getFullName(lead)}` }}
                                  />
                                </TableCell>
                              )}
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>
                                  {getFullName(lead)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{lead.email || "—"}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{lead.phone || "—"}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{getFullAddress(lead)}</Typography>
                              </TableCell>
                              <TableCell>
                                <LeadStatusCell status={lead.status} />
                              </TableCell>
                              {canViewCategory && (
                                <TableCell>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={displayLeadCategoryLabel(lead.category_label)}
                                    sx={leadCategoryChipSx(getLeadCategoryOutlineColor(lead))}
                                  />
                                </TableCell>
                              )}
                              <TableCell>
                                <Typography variant="body2">{lead.territory_name || "—"}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{lead.assigned_rep_name || "—"}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {lead.created_at
                                    ? new Date(lead.created_at).toLocaleDateString()
                                    : "—"}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 2,
                        borderTop: 1,
                        borderColor: "divider",
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.04)"
                            : "grey.50",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Showing {rangeStart}-{rangeEnd} of {totalCount}{" "}
                        {totalCount === 1 ? "lead" : "leads"}
                      </Typography>
                      <Stack
                        direction="row"
                        alignItems="center"
                        flexWrap="wrap"
                        spacing={1}
                        useFlexGap
                      >
                        <FormControl
                          size="small"
                          variant="outlined"
                          sx={{
                            minWidth: 88,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              minHeight: 30.5,
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderRadius: 2,
                            },
                            "& .MuiSelect-select": {
                              py: "5px",
                              pl: 1.25,
                              pr: 3.25,
                              display: "flex",
                              alignItems: "center",
                            },
                          }}
                        >
                          <InputLabel id="search-leads-rows-label">Rows</InputLabel>
                          <Select
                            labelId="search-leads-rows-label"
                            label="Rows"
                            value={rowsPerPage}
                            onChange={handleChangeRowsPerPage}
                          >
                            {ROWS_PER_PAGE_OPTIONS.map((n) => (
                              <MenuItem key={n} value={n}>
                                {n}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          disabled={page === 0}
                          onClick={() => handleChangePage(null, page - 1)}
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: "0.8125rem",
                            minHeight: 30.5,
                            py: "5px",
                            minWidth: 88,
                            ...paginationNavButtonHoverSx,
                          }}
                        >
                          Previous
                        </Button>
                        <Pagination
                          count={totalPages}
                          page={page + 1}
                          onChange={(_, value) =>
                            handleChangePage(null, value - 1)
                          }
                          size="small"
                          color="primary"
                          siblingCount={1}
                          boundaryCount={1}
                          hidePrevButton
                          hideNextButton
                          sx={{
                            "& .MuiPaginationItem-root": {
                              borderRadius: "50%",
                              fontWeight: 600,
                            },
                          }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          disabled={page >= totalPages - 1}
                          onClick={() => handleChangePage(null, page + 1)}
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: "0.8125rem",
                            minHeight: 30.5,
                            py: "5px",
                            minWidth: 88,
                            ...paginationNavButtonHoverSx,
                          }}
                        >
                          Next
                        </Button>
                      </Stack>
                    </Box>
                  </>
                )}
              </Box>
            </>
          )}
        </Card>
      </Box>

      {/* Archive Confirmation Dialog */}
      <ConfirmDialog
        open={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        onConfirm={handleArchiveOldLeads}
        title="Archive Old Leads"
        message="This will archive all leads older than 45 days without a status change. This action cannot be undone. Continue?"
        confirmText="Yes, Archive"
        cancelText="Cancel"
        severity="warning"
      />
      <ConfirmDialog
        open={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        onConfirm={handleBulkDeleteLeads}
        title="Delete Selected Leads"
        message={`Archive ${selectedLeadIds.length} selected lead(s)? They will be removed from active lists and can be restored later.`}
        confirmText={bulkDeleting ? "Archiving..." : "Yes, Delete"}
        cancelText="Cancel"
        severity="error"
      />
      </Box>
    </Box>
  );
}